import { API_REVENUE_PERFORMANCE_EXPORT } from "../../../../utils/apis";
import {
  beautify,
  NoWrap,
  numberFormat,
  returnObject,
} from "../../../../utils/helpers";
import dayjs from "dayjs";

type TRevenuePerformanceProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useRevenuePerformance = ({
  dateFilterKey,
  noWrapColumns = [],
  numberColumns = [],
}: TRevenuePerformanceProps) => {
  const tableColumns = (data: any) => {
    const firstItem = data[0] || {};

    // Custom columns for revenue performance data with specific filters
    const filterColumns = [
      {
        id: "street_name",
        header: <NoWrap>Street Name</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "parking_yard",
        header: <NoWrap>Parking Yard</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "parking_sessions",
        header: <NoWrap>Parking Sessions</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "assessments",
        header: <NoWrap>Assessments</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "collections",
        header: <NoWrap>Collections</NoWrap>,
        filter: { type: "number" },
      },
    ];

    const columns = Object.keys(firstItem).map((column: any) => ({
      id: column,
      header: <NoWrap>{beautify(column)}</NoWrap>,
      filter: [
        "street_name",
        "parking_yard",
        "parking_sessions",
        "assessments",
        "collections",
      ].includes(column)
        ? false
        : true,
    }));

    return [...filterColumns, ...columns];
  };

  const tableData = (data: any) => {
    return data?.data?.map((item: any) => ({
      ...item,
      [dateFilterKey || "created_at"]: (
        <NoWrap>{item[dateFilterKey || "created_at"]}</NoWrap>
      ),
      street_name: <NoWrap>{item.street_name || "N/A"}</NoWrap>,
      parking_yard: (
        <span
          className={`${
            item.parking_yard === "TOTALS"
              ? "font-bold text-blue-800 bg-blue-50 px-2 py-1 rounded"
              : ""
          }`}
        >
          <NoWrap>{item.parking_yard}</NoWrap>
        </span>
      ),
      parking_sessions: (
        <span className="font-medium text-gray-900">
          {numberFormat(item.parking_sessions)}
        </span>
      ),
      assessments: (
        <span className="font-medium text-green-600">
          {numberFormat(item.assessments)}
        </span>
      ),
      collections: (
        <span
          className={`font-medium ${
            item.collections > 0 ? "text-blue-600" : "text-red-500"
          }`}
        >
          {numberFormat(item.collections)}
        </span>
      ),
      collection_rate:
        item.assessments > 0 ? (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              (item.collections / item.assessments) * 100 >= 80
                ? "bg-green-100 text-green-800"
                : (item.collections / item.assessments) * 100 >= 50
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800"
            }`}
          >
            {((item.collections / item.assessments) * 100).toFixed(1)}%
          </span>
        ) : (
          <span className="text-gray-400">N/A</span>
        ),
      ...returnObject(
        noWrapColumns.length > 0,
        noWrapColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = <NoWrap>{item[column]}</NoWrap>;
          }
          return obj;
        }, {}),
      ),
      ...returnObject(
        numberColumns.length > 0,
        numberColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = numberFormat(item[column]);
          }
          return obj;
        }, {}),
      ),
    }));
  };

  // Additional utility functions for revenue performance analysis
  const getRevenueStats = (data: any) => {
    // Filter out totals row for calculations
    const filteredData = data.filter(
      (item: any) => item.parking_yard !== "TOTALS",
    );
    const totalsRow = data.find((item: any) => item.parking_yard === "TOTALS");

    const totalSessions = totalsRow?.parking_sessions || 0;
    const totalAssessments = totalsRow?.assessments || 0;
    const totalCollections = totalsRow?.collections || 0;
    const collectionRate =
      totalAssessments > 0 ? (totalCollections / totalAssessments) * 100 : 0;
    const averageSessionValue =
      totalSessions > 0 ? totalAssessments / totalSessions : 0;

    return {
      totalSessions,
      totalAssessments,
      totalCollections,
      collectionRate: collectionRate.toFixed(1),
      averageSessionValue,
      outstandingAmount: totalAssessments - totalCollections,
      numberOfYards: filteredData.length,
    };
  };

  const getYardPerformance = (data: any) => {
    return data
      .filter((item: any) => item.parking_yard !== "TOTALS")
      .map((item: any) => {
        const collectionRate =
          item.assessments > 0
            ? (item.collections / item.assessments) * 100
            : 0;
        const sessionValue =
          item.parking_sessions > 0
            ? item.assessments / item.parking_sessions
            : 0;

        return {
          ...item,
          collection_rate: collectionRate.toFixed(1),
          average_session_value: sessionValue,
          outstanding_amount: item.assessments - item.collections,
          performance_rating:
            collectionRate >= 80
              ? "Excellent"
              : collectionRate >= 60
                ? "Good"
                : collectionRate >= 40
                  ? "Fair"
                  : "Poor",
        };
      })
      .sort((a: any, b: any) => b.assessments - a.assessments); // Sort by assessments descending
  };

  const getStreetSummary = (data: any) => {
    const streetGroups = data
      .filter((item: any) => item.parking_yard !== "TOTALS")
      .reduce((acc: any, item: any) => {
        const street = item.street_name || "Unknown";
        if (!acc[street]) {
          acc[street] = {
            parking_sessions: 0,
            assessments: 0,
            collections: 0,
            yards: 0,
          };
        }
        acc[street].parking_sessions += item.parking_sessions;
        acc[street].assessments += item.assessments;
        acc[street].collections += item.collections;
        acc[street].yards += 1;
        return acc;
      }, {});

    return Object.entries(streetGroups).map(
      ([street, stats]: [string, any]) => ({
        street_name: street,
        ...stats,
        collection_rate:
          stats.assessments > 0
            ? ((stats.collections / stats.assessments) * 100).toFixed(1)
            : "0.0",
        average_session_value:
          stats.parking_sessions > 0
            ? (stats.assessments / stats.parking_sessions).toFixed(0)
            : "0",
      }),
    );
  };

  const exportData = {
    api: API_REVENUE_PERFORMANCE_EXPORT,
    fileName: `revenue_preformance_report${new Date().toISOString().split("T")[0]}.xlsx`,
    showCustomDateRange: true,
    start_date: "start_date",
    end_date: "end_date",
  };
  const tableTitle = "Revenue Performance Report";
  const initialFilters = {
    start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
    end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
  };
  const externalFilters = [
    {
      id: "payment_date",
      label: "Payment Date Range",
      type: "date",
      options: [],
      dateRangeNames: {
        startDateKey: "start_date",
        endDateKey: "end_date",
      },
    },
  ];
  return {
    tableColumns,
    exportData,
    tableData,
    tableTitle,
    initialFilters,
    externalFilters,
    getRevenueStats,
    getYardPerformance,
    getStreetSummary,
  };
};

export default useRevenuePerformance;
